import { useCallback, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useOptionalStores } from "@/stores/useStores";
import type { AddVersionsOutcome, StagedVersionItem } from "../types/addVersions";
import { hasVersionHistory } from "../utils/versionHistory";

const NOTHING_COMMITTED: AddVersionsOutcome = { committed: 0, touchedVersionGroups: [] };

/** Which of the two version dialogs is showing for the target. */
export type VersionDialogView = "add" | "versioning";

/**
 * The state a view needs to host the version dialogs: which document is
 * targeted, which dialog shows, whether a commit is in flight, and the
 * handlers.
 *
 * A document that already has versions opens "Versioning" (reorder, make a
 * version a separate file, or go on to add more); a single-version one opens
 * "Add new version" straight away, as it always has.
 *
 * The add dialog owns staging and ordering; the view only owns the target.
 * It closes once EVERY staged item landed — after a part-way failure it stays
 * open with the rest still listed, and after an unexpected throw with
 * everything still listed. Reached from Versioning, it returns there instead.
 */
export function useAddNewVersionsDialog<
  TDoc extends { version?: number | null; version_group?: string | null },
>(
  onAddNewVersions:
    | ((items: StagedVersionItem[], doc: TDoc) => Promise<AddVersionsOutcome>)
    | undefined,
  /** Lineages a commit changed, for the view to drop its cached chains. */
  onLineagesChanged?: (versionGroups: string[]) => void,
  /** How the host's rows carry `folder_id`. */
  folderIdKind: "string" | "number" = "string"
) {
  const { toast } = useToast();
  const { t } = useLanguage();
  const documentStore = useOptionalStores()?.documentStore ?? null;
  const [target, setTarget] = useState<TDoc | null>(null);
  const [view, setView] = useState<VersionDialogView>("add");
  // Set when "Add versions" was chosen from Versioning: a finished add goes
  // back there rather than closing.
  const [cameFromVersioning, setCameFromVersioning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const openFor = useCallback((doc: TDoc | null) => {
    setTarget(doc);
    setView(doc && hasVersionHistory(doc) ? "versioning" : "add");
    setCameFromVersioning(false);
  }, []);

  const showAdd = useCallback(() => {
    setView("add");
    setCameFromVersioning(true);
  }, []);

  // Versioning found nothing to arrange: "Add new version" takes its place,
  // as if it had been opened directly, so there is nothing to go back to.
  const replaceWithAdd = useCallback(() => {
    setView("add");
    setCameFromVersioning(false);
  }, []);

  // The target itself became a separate file: staged versions are for it,
  // so they follow it into its own lineage (and folder) rather than being
  // filed into the lineage it left. The folder id keeps the host's own
  // representation: the list's rows carry it as a string, the timeline's as a
  // number.
  const retarget = useCallback(
    (moved: { versionGroup: string; filename: string; folderId?: number | null }) => {
      setTarget((doc) => {
        if (!doc) return doc;
        const folder =
          "folder_id" in doc && moved.folderId !== undefined
            ? {
                folder_id:
                  moved.folderId === null || folderIdKind === "number"
                    ? moved.folderId
                    : String(moved.folderId),
              }
            : {};
        return {
          ...doc,
          version_group: moved.versionGroup,
          version: 1,
          ...("filename" in doc ? { filename: moved.filename } : {}),
          ...folder,
        };
      });
    },
    [folderIdKind]
  );

  const showVersioning = useCallback(() => {
    setView("versioning");
    setCameFromVersioning(false);
  }, []);

  const confirm = useCallback(
    async (items: StagedVersionItem[]): Promise<AddVersionsOutcome> => {
      const doc = target;
      if (!doc || !onAddNewVersions) return NOTHING_COMMITTED;

      setIsSubmitting(true);
      try {
        const outcome = await onAddNewVersions(items, doc);
        onLineagesChanged?.(outcome.touchedVersionGroups);
        if (outcome.committed === items.length) {
          if (cameFromVersioning) showVersioning();
          else setTarget(null);
        }
        return outcome;
      } catch (error) {
        // The commit chain toasts its own known failures and resolves rather
        // than rejecting, so this only fires on an *unexpected* throw. Surface
        // it instead of letting it become a silent unhandled rejection, and
        // keep the staged, ordered list for a retry.
        console.error("[useAddNewVersionsDialog] Adding new versions failed:", error);
        toast({
          variant: "destructive",
          title: t.common.error,
          description: error instanceof Error ? error.message : t.common.unexpectedError,
        });
        return NOTHING_COMMITTED;
      } finally {
        setIsSubmitting(false);
      }
    },
    [target, onAddNewVersions, onLineagesChanged, cameFromVersioning, showVersioning, toast, t]
  );

  /**
   * Versioning saved a reorder or a detach. Rows changed lineage, number and
   * possibly folder, so cached chains go and the visible list is re-read
   * rather than left to realtime, which can drop events.
   */
  const onVersionsEdited = useCallback(
    (versionGroups: string[]) => {
      onLineagesChanged?.(versionGroups);
      documentStore?.revalidateVisibleDocuments(true);
    },
    [onLineagesChanged, documentStore]
  );

  const onOpenChange = useCallback(
    (open: boolean) => {
      // Closing mid-commit would hide the only place its outcome is reported.
      if (!open && !isSubmitting) setTarget(null);
    },
    [isSubmitting]
  );

  return {
    target,
    view,
    openFor,
    showAdd,
    replaceWithAdd,
    retarget,
    showVersioning,
    cameFromVersioning,
    isSubmitting,
    confirm,
    onVersionsEdited,
    onOpenChange,
  };
}
